/**
 * Aba Autonomia em leitura (Item 368 F2).
 *
 * Exibe a fase de autonomia da IA (`autonomyPhase` — manual|proposal|autonomous)
 * com label e descrição canônicos. O modo de operação técnico (H_a..H_d) e o
 * modo de validação (`validationMode`) permanecem visíveis como referência de
 * contexto — apenas a fase de autonomia tem destaque editorial nesta aba.
 */

import { useCapability } from '../../../hooks/use-capability';
import {
  AUTONOMY_PHASE_LABELS,
  NATIVE_SCHEDULING_MODE_OPTIONS,
  WAITLIST_PROMOTION_MODE_OPTIONS,
  validationModeLabel,
} from '../playbook-labels';
import { ValidationModeBadge } from '../PlaybookBadges';

import type { PlaybookAutonomyPhase, PlaybookDto } from '../types';

const PHASE_ORDER: PlaybookAutonomyPhase[] = ['manual', 'proposal', 'autonomous'];

export function TabOperation({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const current = playbook.autonomyPhase;
  const validation = validationModeLabel(playbook.validationMode);
  const canNative = useCapability('scheduling-native');
  const canWaitlist = useCapability('scheduling-waitlist');
  const nativeLabel =
    NATIVE_SCHEDULING_MODE_OPTIONS.find((o) => o.value === playbook.nativeSchedulingMode)?.label ??
    'Herdar do tenant';
  const promotionLabel =
    WAITLIST_PROMOTION_MODE_OPTIONS.find((o) => o.value === playbook.waitlistPromotionMode)?.label ??
    'Herdar do tenant';

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Autonomia">
      <h2 className="pb-panel__title">Autonomia</h2>

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Fase de autonomia da IA</span>
        <div className="pb-radio-row">
          {PHASE_ORDER.map((phase) => {
            const meta = AUTONOMY_PHASE_LABELS[phase];
            const selected = phase === current;
            return (
              <div
                key={phase}
                className={'pb-radio' + (selected ? ' pb-radio--selected' : '')}
                aria-current={selected ? 'true' : undefined}
              >
                <span className="pb-radio__dot" />
                <span className="pb-radio__main">
                  <span className="pb-radio__label">{meta.label}</span>
                  <span className="pb-radio__desc">{meta.desc}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Modo de validação</span>
        <div className="pb-inline-value">
          <ValidationModeBadge mode={playbook.validationMode} />
          <div className="pb-inline-value__text">
            <span className="pb-read-value">{validation.label}</span>
            {validation.desc !== '' && (
              <span className="pb-read-value pb-read-value--muted">{validation.desc}</span>
            )}
          </div>
        </div>
      </div>

      {/* Frente B Plano 3 (B2) — override de agenda nativa por-playbook (gated). */}
      {canNative && (
        <>
          <div className="pb-panel__divider" />
          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Agenda nativa — modo de exposição</span>
            <span className="pb-read-value">{nativeLabel}</span>
          </div>
          {canWaitlist && (
            <div className="pb-panel__section">
              <span className="pb-panel__section-label">Fila de espera — modo de promoção</span>
              <span className="pb-read-value">{promotionLabel}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
